<template>
	<div id="soundPlayer" class="soundPlayer" :class="darkMode ? 'darkMode' : ''">
		<div class="soundPlayer-content">
			<div v-if=" draggable " class="soundPlayer-draggable-btn">
				<move-icon />
			</div>

			<div>
				<img class="object-contain rounded p-2 shadow border h-110px max-h-110px max-w-110px max-h-110px max-w-110px w-100% md:flex hidden"
					:src="current?.mainOutcome?.imageSrc" alt="" />
			</div>
			<div class="flex-1 w-100%">
				<div class="flex sm:flex-row flex-col items-center flex-1 mb-2 flex-1 mx-3">
					<div class="flex-1 w-100% md:flex flex-col hidden">
						<p class="text-primary font-semibold">{{ current?.mainOutcome?.selectHeader }}</p>
						<h2 class="text-sm font-600 md:text-lg">{{ current?.mainOutcome?.title }}</h2>
					</div>
					<div class="soundPlayer-timer text-dark ml-auto order-first md:order-last">{{ formattedTime }}</div>
				</div>

				<div class="flex items-center flex-1 w-100% sm:flex-row flex-col">
					<div class="flex flex-1 w-100% items-center justify-between">
						<div class="soundPlayer-controls mx-3  mb-2 md:mb-0">
							<button v-if=" isLoading " class="soundPlayer-actions-btn">
								<div class="spinner">
									<div class="spinner-border"></div>
								</div>
							</button>
							<button v-else-if=" isPlaying " @click="pause()" class="soundPlayer-actions-btn active">
								<pause-icon :size="30" />

							</button>
							<button v-else @click.prevent="play()" class="soundPlayer-actions-btn">
								<play-icon :size="30" />
							</button>
						</div>
						<div class="flex-1">
							<div class="soundPlayer-actions flex-col md:flex-row">
								<input type="range" min="0" max="100" class="soundPlayer-slider" :value="slider"
									@input="onSliderInput" @mousedown="isSeeking = true" @mouseup="onSeekEnd"
									@touchstart="isSeeking = true" @touchend="onSeekEnd" />
							</div>
						</div>
						<button class="soundPlayer-actions-btn soundPlayer-volume ml-auto">
							<volume-icon />
							<div class="soundPlayer-volume-box">
								<input v-model="volume" type="range" min="0" max="100" class="soundPlayer-volume-slider"
									@input="changeVolume" />
							</div>
						</button>
					</div>
					<div class="flex md:flex-row gap-2">
						<button
							@click="toggleShuffleMode"
							class="soundPlayer-actions-btn border !ml-2 text-dark !hover:text-white"
							:title="'Shuffle: ' + (shuffleMode ? 'on' : 'off')"
							:class="{ '!text-success': shuffleMode }"

						>
							<span v-if="shuffleMode"><shuffle-icon :size="20" /></span>
							<span v-else><not-shuffle-icon class="text-success" :size="20"/></span>
						</button>

						<button class="soundPlayer-actions-btn border !ml-2 "
							@click="changeSpeed">
							{{ speeds[speedIndex] }}x
						</button>
						<button @click="toggleRepeatMode" class="soundPlayer-actions-btn border !ml-2 text-dark !hover:text-white"
							:title="'Repeat: ' + repeatMode"
							:class="{ '!text-success': repeatMode !== 'none' }"
							>
							<template v-if=" repeatMode === 'one' "><repeat-once :size="20"/></template>
							<template v-else-if=" repeatMode === 'all' "><repeat-all :size="20"/></template>
							<template v-else><repeat-all class="text-danger" :size="20"/></template>
						</button>

						<button @click="prevSound()" class="soundPlayer-actions-btn border !ml-2 ">
							<prev-icon />
						</button>
						<button @click="nextSound()" class="soundPlayer-actions-btn border !ml-2 ">
							<next-icon />
						</button>
						<button v-if=" showSoundList " class="soundPlayer-actions-btn border !ml-2 "
							@click="showSoundList = false">
							<close-icon />
						</button>
						<button v-else
							class="soundPlayer-actions-btn border !ml-2 md:px-[1rem] px-[0.5rem] py-[0.5rem] "
							@click="showSoundList = true">
							<list-icon />
						</button>
					</div>

				</div>
			</div>
		</div>
		<div v-if=" !showSoundList " class="soundPlayer-soundList">
			<div class="soundPlayer-soundList-content">
				<button v-for=" soundItem in filteredSounds " :key="soundItem.identifier"
					class="soundPlayer-soundList-item flex items-start w-full !p-15px"
					:class="current.identifier === soundItem.identifier && isPlaying ? 'active' : ''"
					@click="current.identifier !== soundItem.identifier ? play( soundItem ) : isPlaying ? pause() : play()"
					@mouseenter="hoveredIdentifier = soundItem.identifier" @mouseleave="hoveredIdentifier = null">
					<div class="soundPlayer-icon-wrapper relative">
						<template v-if=" current.identifier === soundItem.identifier && isPlaying ">
							<div class="soundPlayer-icon-wrapper relative">
								<pause-circle-icon v-if=" hoveredIdentifier !== soundItem.identifier " :size="20"
									class="soundPlayer-soundList-item-icon self-center mr-2" />
								<pause-circle-icon-hover v-else :size="20"
									class="soundPlayer-soundList-item-icon-hover self-center mr-2" />
							</div>
						</template>

						<play-circle-icon v-else :size="20" class="soundPlayer-soundList-item-icon self-center mr-2" />
					</div>

					<div class="w-100px md:flex items-center justify-center flex-col p-1px ml-2 hidden">
						<img class="object-contain h-90px max-h-70px w-100%" :src="soundItem.mainOutcome.imageSrc"
							alt="" />
					</div>
					<div class="ml-2 text-left">
						<p class="text-primary font-semibold">{{ soundItem.mainOutcome.selectHeader }}</p>
						<h2 class="text-sm text-dark font-600 md:text-md mb-1"
							:class="{ 'text-primary': current.identifier === soundItem.identifier && isPlaying }">
							{{ soundItem.mainOutcome.title }}
						</h2>
						<small v-if=" soundItem.mainOutcome.publishingDate "
							class="text-success text-xs fw-500 text-left mr-2">Published: {{
								formatDateEU( soundItem.mainOutcome.publishingDate ) }}</small>
						<small v-if=" soundItem.mainOutcome.author " class="text-success text-xs fw-500 text-left">Author:
							{{
								soundItem.mainOutcome.author }}</small>
					</div>
					<div class="soundPlayer-timer text-dark ml-auto">
						<span v-if=" current.identifier === soundItem.identifier && isPlaying "
							:class="{ 'text-success': current.identifier === soundItem.identifier && isPlaying }">{{
								formattedTime }}</span>
						<span v-else>{{ soundItem.duration }}</span>
					</div>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, defineEmits, onBeforeUnmount, computed } from 'vue';
import PlayIcon from './icons/PlayIcon.vue';
import PauseIcon from './icons/PauseIcon.vue';
import PrevIcon from './icons/PrevIcon.vue';
import NextIcon from './icons/NextIcon.vue';
import VolumeIcon from './icons/VolumeIcon.vue';
import ListIcon from './icons/ListIcon.vue';
import CloseIcon from './icons/CloseIcon.vue';
import PlayCircleIcon from './icons/PlayCircleIcon.vue';
import PauseCircleIcon from './icons/PauseCircleIcon.vue';
import PauseCircleIconHover from './icons/PauseCircleIconHover.vue';
import MoveIcon from './icons/MoveIcon.vue';
import RepeatOnce from './icons/RepeatOnce.vue';
import RepeatAll from './icons/RepeatAll.vue';
import ShuffleIcon from './icons/ShuffleIcon.vue';
import NotShuffleIcon from './icons/NotShuffleIcon.vue';

interface SoundItem {
  identifier: string;
  file_url: string;
  duration?: string;
  mainOutcome: {
    selectHeader: string;
    title: string;
    imageSrc: string;
    publishingDate?: string;
    author?: string;
  };
  podcastSlideshow?: {
    slideMusic?: boolean;
  };
}

const props = defineProps<{
  sounds: SoundItem[];
  darkMode?: boolean;
  draggable?: boolean;
}>();



const emits = defineEmits(['activeSound']);
const showSoundList = ref(false);
const index = ref(0);
const filteredSounds = computed<SoundItem[]>(() =>
  props.sounds.filter((item) => item.file_url && item.file_url.trim() !== '')
);

const current = ref<SoundItem | null>(filteredSounds.value[index.value] || null);


const isPlaying = ref(false);
const isLoading = ref(false);
const player = new Audio();
const speedIndex = ref(0);
const speeds = [1, 1.5, 2, 0.5];
const slider = ref(0);
const isSeeking = ref(false);
const volume = ref(70);
const shuffleMode = ref(localStorage.getItem('shuffleMode') === 'true');

const shuffledIndexes = ref<number[]>([]);
const repeatModes = ['none', 'one', 'all'];
const repeatIndex = ref(Number(localStorage.getItem('repeatIndex')) || 0);
const repeatMode = computed(() => repeatModes[repeatIndex.value]);
const hoveredIdentifier = ref<string | null>(null);


const shuffleArray = (length: number) => {
  const array = Array.from({ length }, (_, i) => i);
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const toggleShuffleMode = () => {
  shuffleMode.value = !shuffleMode.value;

  if (shuffleMode.value) {
    shuffledIndexes.value = shuffleArray(filteredSounds.value.length);
    index.value = shuffledIndexes.value[0];
  } else {
	index.value = current.value
  ? filteredSounds.value.findIndex(item => item.identifier === current.value.identifier)
  : 0;
  }
};

const toggleRepeatMode = () => {
  repeatIndex.value = (repeatIndex.value + 1) % repeatModes.length;
  localStorage.setItem('repeatIndex', repeatIndex.value.toString());
};

const updateSliderStyle = () => {
  const percent = slider.value + '%';
  document.documentElement.style.setProperty('--slider-progress', percent);
};

const onSliderInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  slider.value = parseFloat(target.value);
  updateSliderStyle();
  if (isPlaying.value) player.pause();
};

const onSeekEnd = () => {
  if (!player.duration || isNaN(player.duration)) return;
  player.currentTime = (slider.value / 100) * player.duration;
  if (isPlaying.value) player.play();
  isSeeking.value = false;
};

const changeVolume = (event: Event) => {
  const target = event.target as HTMLInputElement;
  player.volume = parseFloat(target.value) / 100;
};

const formatDateEU = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}.${String(
    date.getMonth() + 1
  ).padStart(2, '0')}.${date.getFullYear()}`;
};


const play = (song?: SoundItem) => {
  if (song && song.identifier !== current.value?.identifier) {
    current.value = song;
    player.src = song.file_url;
    player.load();
    slider.value = 0;
  }

  isLoading.value = true;

  // gjithmonë cakto oncanplay para play()
  player.oncanplay = () => {
    player.play();
    isPlaying.value = true;
    isLoading.value = false;
    updateSliderStyle();
  };

  // nëse nuk e ndryshuam src, duhet ta ngarkojmë prap
  if (!song) {
    player.load();
  }

  if (song) {
    index.value = filteredSounds.value.findIndex(
      (item) => item.identifier === song.identifier
    );
  }
};


const formattedTime = ref('00:00');
const padTime = (time: number) => time.toString().padStart(2, '0');
const updateFormattedTime = () => {
  const currentTime = player.currentTime;
  formattedTime.value = `${padTime(Math.floor(currentTime / 60))}:${padTime(
    Math.floor(currentTime % 60)
  )}`;
};

const getMediaDuration = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => resolve(audio.duration);
    audio.onerror = () => reject(new Error('Could not load media metadata'));
    audio.src = url;
  });
};

const nextSound = () => {
  if (shuffleMode.value) {
    const currentPos = shuffledIndexes.value.indexOf(index.value);
    const nextPos = (currentPos + 1) % shuffledIndexes.value.length;
    index.value = shuffledIndexes.value[nextPos];
  } else {
    index.value = (index.value + 1) % filteredSounds.value.length;
  }
  play(filteredSounds.value[index.value]);
};

const prevSound = () => {
  if (shuffleMode.value) {
    const currentPos = shuffledIndexes.value.indexOf(index.value);
    const prevPos =
      (currentPos - 1 + shuffledIndexes.value.length) % shuffledIndexes.value.length;
    index.value = shuffledIndexes.value[prevPos];
  } else {
    index.value =
      (index.value - 1 + filteredSounds.value.length) % filteredSounds.value.length;
  }
  play(filteredSounds.value[index.value]);
};

const pause = () => {
  player.pause();
  isPlaying.value = false;
};

const changeSpeed = () => {
  speedIndex.value = (speedIndex.value + 1) % speeds.length;
  player.playbackRate = speeds[speedIndex.value];
};

onMounted(async () => {
	if (shuffleMode.value) {
		shuffledIndexes.value = shuffleArray(filteredSounds.value.length);
		index.value = shuffledIndexes.value[0];
	}

  for (const soundItem of props.sounds) {
	
    try {
      const duration = await getMediaDuration(soundItem.file_url);
      soundItem.duration = `${Math.floor(duration / 60)}:${String(
        Math.floor(duration % 60)
      ).padStart(2, '0')}`;
    } catch {
      soundItem.duration = 'N/A';
    }
  }
  if (filteredSounds.value.length) {
    current.value = filteredSounds.value[index.value];
    player.src = current.value.file_url;
  }
  player.addEventListener('ended', () => {
    if (repeatMode.value === 'one') {
      player.pause();
      player.currentTime = 0;
      setTimeout(() => {
        player.play();
        slider.value = 0;
        formattedTime.value = '00:00';
        updateSliderStyle();
      }, 100);
    } else if (repeatMode.value === 'all') {
      nextSound();
    } else {
      isPlaying.value = false;
    }
  });
  player.addEventListener('timeupdate', () => {
    if (player.duration && !isSeeking.value) {
      slider.value = (player.currentTime / player.duration) * 100;
      updateSliderStyle();
      updateFormattedTime();
    }
    isLoading.value = false;
  });
  player.volume = volume.value / 100;
});

watch(
  () => props.sounds,
  () => {
    if (filteredSounds.value.length) {
      current.value = filteredSounds.value[index.value];
      player.src = current.value.file_url;
    }
  }
);

watch(
  () => current.value,
  () => {
    emits('activeSound', current.value);
  }
);
watch(shuffleMode, (value) => {
  localStorage.setItem('shuffleMode', value.toString());
});


onBeforeUnmount(() => {
  player.pause();
  localStorage.removeItem('repeatIndex');
  localStorage.removeItem('shuffleMode'); 

});
</script>

<style>
@import '../css/main.css';

.spinner-border {
	border: 2px solid rgba(255, 255, 255, 0.2);
	border-left-color: #fff;
	border-radius: 50%;
	width: 30px;
	height: 30px;
	animation: spin 0.6s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

.soundPlayer-soundList-item-icon,
.soundPlayer-soundList-item-icon-hover {
	margin: 0 !important;
	transition: opacity 0.3s ease, transform 0.3s ease;
}

.soundPlayer-icon-wrapper:hover .soundPlayer-soundList-item-icon,
.soundPlayer-icon-wrapper:hover .soundPlayer-soundList-item-icon-hover {
	transform: scale(1.1);
	/* pak zmadhim në hover */
	opacity: 0.8;
	/* pak transparencë në hover */
}

.soundPlayer-slider {
	width: 100%;
	cursor: pointer;
}

.soundPlayer-volume-slider {
	width: 100%;
	cursor: pointer;
}

.soundPlayer-actions-btn[title*="Repeat: one"]:hover,
.soundPlayer-actions-btn[title*="Repeat: all"]:hover,
.soundPlayer-actions-btn svg.text-success:hover {
	color: white!important;
	background-color: #4caf50 !important;
	transition: background-color 0.3s ease, color 0.3s ease;
}
.soundPlayer-actions-btn:hover {
	color: white!important;
}
</style>
